/**
 * Report document subscriptions — Phase 6 replacement.
 * Delegates to /api/reports/* on dash-api instead of Firestore.
 */
import {useEffect, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {'Authorization': `Bearer ${t}`} : {}; }

export function useReportDocumentSubscription(reportId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!reportId) { setData(null); setLoading(false); setError(null); return; }
        setLoading(true); setError(null);
        let cancelled = false;
        fetch(`${BASE}/api/reports/${encodeURIComponent(reportId)}`, {headers: _authH()})
            .then(r => r.status === 404 ? null : r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
            .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
            .catch(err => { if (!cancelled) { setError(err); setLoading(false); } });
        return () => { cancelled = true; };
    }, [reportId]);

    return {data, loading, error};
}

export function parseVersionFromSnapshotPath(snapshotPath) {
    if (!snapshotPath) return null;
    const match = snapshotPath.match(/ver_(\d+)\.bin$/);
    return match ? match[1] : null;
}

export function generateVersionNumbers(latestVersion) {
    if (!latestVersion) return [];
    const latest = parseInt(latestVersion, 10);
    if (isNaN(latest) || latest < 1) return [];
    const versions = [];
    for (let i = latest; i >= 1; i--) {
        versions.push(String(i).padStart(latestVersion.length, '0'));
    }
    return versions;
}

export function buildVersionStoragePath(reportId, version) {
    return `documents/${reportId}/versions/ver_${version}.bin`;
}

export function useReportVersionsSubscription(reportId) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!reportId) { setData([]); setLoading(false); setError(null); return; }
        setLoading(true); setError(null);
        let cancelled = false;
        fetch(`${BASE}/api/reports/${encodeURIComponent(reportId)}/versions`, {headers: _authH()})
            .then(r => r.ok ? r.json() : [])
            .then(d => { if (!cancelled) { setData(Array.isArray(d) ? d : []); setLoading(false); } })
            .catch(err => { if (!cancelled) { setError(err); setLoading(false); } });
        return () => { cancelled = true; };
    }, [reportId]);

    return {data, loading, error};
}

export function formatVersionNumber(version, width = 7) {
    if (version === null || version === undefined) return null;
    const n = typeof version === 'string' ? parseInt(version, 10) : version;
    if (!Number.isFinite(n)) return null;
    return String(n).padStart(width, '0');
}

export function useReportVersionDocSubscription(reportId, versionId) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!reportId || !versionId) { setData(null); setLoading(false); setError(null); return; }
        setLoading(true); setError(null);
        let cancelled = false;
        const versionNum = parseInt(String(versionId), 10);
        const versionPath = Number.isFinite(versionNum) ? versionNum : encodeURIComponent(versionId);
        fetch(`${BASE}/api/reports/${encodeURIComponent(reportId)}/versions/${versionPath}`, {headers: _authH()})
            .then(r => r.status === 404 ? null : r.ok ? r.json() : null)
            .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
            .catch(err => { if (!cancelled) { setError(err); setLoading(false); } });
        return () => { cancelled = true; };
    }, [reportId, versionId]);

    return {data, loading, error};
}
